
import { useState } from 'react';
import { executeAgentCapability, streamCapabilityExecution, CapabilityExecutionOptions, ModelProvider } from '@/services/agentic/capabilityExecutionService';
import { AgentCapability } from '@/types/agentic';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '@/integrations/supabase/client';

interface UseAgentCapabilityOptions {
  defaultProvider?: ModelProvider;
  defaultModel?: string;
}

export const useAgentCapability = (
  capability: AgentCapability | null,
  options: UseAgentCapabilityOptions = {}
) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [result, setResult] = useState<any>(null);
  const [streamingResult, setStreamingResult] = useState<any>(null);
  
  const executeCapability = async (
    query: string,
    executionOptions: CapabilityExecutionOptions = {}
  ) => {
    if (!capability) {
      throw new Error('No capability provided');
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Set default model based on provider if not specified
      let modelToUse = executionOptions.model || options.defaultModel;
      const providerToUse = executionOptions.provider || options.defaultProvider || 'openai';
      
      if (!modelToUse) {
        // Default models by provider
        switch (providerToUse) {
          case 'openai':
            modelToUse = 'gpt-4o-mini';
            break;
          case 'anthropic':
            modelToUse = 'claude-3-5-sonnet-latest';
            break;
          case 'openrouter':
            modelToUse = 'openrouter-default';
            break;
        }
      }
      
      const response = await executeAgentCapability(
        capability,
        query,
        {
          provider: providerToUse,
          model: modelToUse,
          ...executionOptions
        }
      );
      
      setResult(response);
      return response;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  const streamCapability = async (
    query: string,
    executionOptions: CapabilityExecutionOptions = {}
  ) => {
    if (!capability) {
      throw new Error('No capability provided');
    }
    
    setLoading(true);
    setError(null);
    setStreamingResult(null);
    
    // Generate a unique execution ID
    const executionId = uuidv4();
    
    // Subscribe to the realtime channel for this execution
    const channel = supabase
      .channel(`capability-execution-${executionId}`)
      .on('broadcast', { event: 'execution_status' }, (payload) => {
        console.log('Execution status update:', payload);
        // Update UI based on status
      })
      .on('broadcast', { event: 'execution_result' }, (payload) => {
        console.log('Execution result:', payload);
        setStreamingResult(payload.payload.result);
        setResult(payload.payload.result);
      })
      .subscribe();
    
    try {
      // Set default model based on provider if not specified
      let modelToUse = executionOptions.model || options.defaultModel;
      const providerToUse = executionOptions.provider || options.defaultProvider || 'openai';
      
      if (!modelToUse) {
        // Default models by provider
        switch (providerToUse) {
          case 'openai':
            modelToUse = 'gpt-4o-mini';
            break;
          case 'anthropic':
            modelToUse = 'claude-3-5-sonnet-latest';
            break;
          case 'openrouter':
            modelToUse = 'openrouter-default';
            break;
        }
      }
      
      // Start the capability execution
      const response = await streamCapabilityExecution(
        executionId,
        capability,
        query,
        {
          provider: providerToUse,
          model: modelToUse,
          ...executionOptions
        }
      );
      
      return response;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
      // Unsubscribe from the channel
      supabase.removeChannel(channel);
    }
  };
  
  return {
    loading,
    error,
    result,
    streamingResult,
    executeCapability,
    streamCapability
  };
};
